import { LocalStorageKeys } from '@consts';

export class LocalStorageService {
  private static instance: LocalStorageService;

  public static getInstance(): LocalStorageService {
    if (!LocalStorageService.instance) {
      LocalStorageService.instance = new LocalStorageService();
    }
    return LocalStorageService.instance;
  }

  private getLocalStorageKey = (userId: number, key: string) => {
    return `@imprv.ai.${userId.toString()}::${key}`;
  };

  private isClient = (): boolean => {
    return typeof window === 'object';
  };

  private writeToLocalStorage = (userId: number, key: string, value: any) => {
    if (!this.isClient()) {
      return;
    }

    const localStorageKey = this.getLocalStorageKey(userId, key);

    if (!localStorageKey) {
      return;
    }

    localStorage.setItem(localStorageKey, value);
  };

  private readFromLocalStorage = (userId: number, key: string) => {
    if (!this.isClient()) {
      return null;
    }

    const localStorageKey = this.getLocalStorageKey(userId, key);

    if (!localStorageKey) {
      return null;
    }

    return localStorage.getItem(localStorageKey);
  };

  private removeFromLocalStorage = (userId: number, key: string) => {
    if (!this.isClient()) {
      return;
    }

    const localStorageKey = this.getLocalStorageKey(userId, key);

    localStorage.removeItem(localStorageKey);
  };

  /**
   * STORAGE - INTRO IS DONE
   */
  public getIntroIsDone = (userId: number): boolean => {
    return this.readFromLocalStorage(userId, LocalStorageKeys.INTRO_IS_DONE) === 'true';
  };

  public setIntroIsDone = (userId: number, value: boolean) => {
    this.writeToLocalStorage(userId, LocalStorageKeys.INTRO_IS_DONE, value.toString());
  };

  /**
   * STORAGE - LAST SCHEDULE QUEUE DATE
   */
  public getLastScheduleQueueDate = (userId: number): string => {
    return this.readFromLocalStorage(userId, LocalStorageKeys.LAST_SCHEDULE_QUEUE_DATE);
  };

  public setLastScheduleQueueDate = (userId: number, value: string) => {
    this.writeToLocalStorage(userId, LocalStorageKeys.LAST_SCHEDULE_QUEUE_DATE, value);
  };

  /**
   * STORAGE - VIDEO PLAYBACK SPEED
   */
  public getVideoPlaybackSpeed = (userId: number): number => {
    try {
      const savedSpeed =
        this.readFromLocalStorage(userId, LocalStorageKeys.VIDEO_PLAYBACK_SPEED) || '1';
      let speed = Number.parseFloat(savedSpeed);
      if (isNaN(speed) || ![0.5, 0.75, 1, 1.25, 1.5, 2].includes(speed)) {
        speed = 1;
      }
      return speed;
    } catch (error) {
      console.error('Error loading video playback speed:', error);
    }
    return 1;
  };

  public setVideoPlaybackSpeed = (userId: number, value: number) => {
    this.writeToLocalStorage(userId, LocalStorageKeys.VIDEO_PLAYBACK_SPEED, value.toString());
  };

  /**
   * STORAGE - COLLAPSED TOC SECTIONS
   */
  public isTocSectionCollapsed = (userId: number, sectionId: string): boolean => {
    const collapsedSections = this.getCollapsedTocSections(userId);
    return collapsedSections.has(sectionId);
  };

  public setTocSectionCollapsed = (userId: number, sectionId: string, collapsed: boolean) => {
    const collapsedSections = this.getCollapsedTocSections(userId);

    if (collapsed) {
      // Add to collapsed set
      collapsedSections.add(sectionId);
      this.saveCollapsedTocSections(userId, collapsedSections);
    } else if (collapsedSections.has(sectionId)) {
      // Remove from collapsed set
      collapsedSections.delete(sectionId);
      this.saveCollapsedTocSections(userId, collapsedSections);
    }
  };

  private getCollapsedTocSections = (userId: number): Set<string> => {
    const stored = this.readFromLocalStorage(userId, LocalStorageKeys.COLLAPSED_SECTIONS);
    if (!stored) return new Set();

    try {
      const parsed = JSON.parse(stored);
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch (error) {
      console.error('Error parsing collapsed TOC sections:', error);
      return new Set();
    }
  };

  private saveCollapsedTocSections = (userId: number, sections: Set<string>) => {
    this.writeToLocalStorage(
      userId,
      LocalStorageKeys.COLLAPSED_SECTIONS,
      JSON.stringify(Array.from(sections))
    );
  };

  /**
   * STORAGE - LAST NODE ENTITY ID (per goal subscription)
   */
  public getLastNodeEntityId = (userId: number, goalSubscriptionId: number): string | null => {
    const stored = this.readFromLocalStorage(
      userId,
      `${LocalStorageKeys.LAST_NODE_ENTITY_ID}_${goalSubscriptionId}`
    );
    return stored;
  };

  public setLastNodeEntityId = (
    userId: number,
    goalSubscriptionId: number,
    nodeEntityId: string
  ) => {
    this.writeToLocalStorage(
      userId,
      `${LocalStorageKeys.LAST_NODE_ENTITY_ID}_${goalSubscriptionId}`,
      nodeEntityId
    );
  };

  /**
   * STORAGE - LAST EDITED NODE ENTITY ID (per strategy)
   */

  public getLastEditedNodeEntityId = (userId: number, strategyEntityId: string): string | null => {
    const stored = this.readFromLocalStorage(
      userId,
      `${LocalStorageKeys.LAST_EDITED_NODE_ENTITY_ID}_${strategyEntityId}`
    );
    return stored;
  };

  public setLastEditedNodeEntityId = (
    userId: number,
    strategyEntityId: string,
    nodeEntityId: string
  ) => {
    this.writeToLocalStorage(
      userId,
      `${LocalStorageKeys.LAST_EDITED_NODE_ENTITY_ID}_${strategyEntityId}`,
      nodeEntityId
    );
  };

  /**
   * STORAGE - GOAL RECOMMENDATION COUNT HIDDEN UNTIL (per caller: banner | count)
   * ISO date string: when set, the banner or nav badge is hidden until this date (e.g. one week).
   */
  public getGoalRecommendationCountHidden = (
    userId: number,
    caller: 'banner' | 'count'
  ): string | null => {
    const key =
      caller === 'banner'
        ? LocalStorageKeys.GOAL_RECOMMENDATION_BANNER_HIDDEN_UNTIL
        : LocalStorageKeys.GOAL_RECOMMENDATION_COUNT_BADGE_HIDDEN_UNTIL;
    return this.readFromLocalStorage(userId, key);
  };

  public setGoalRecommendationCountHidden = (
    userId: number,
    caller: 'banner' | 'count',
    isoDateString: string
  ): void => {
    const key =
      caller === 'banner'
        ? LocalStorageKeys.GOAL_RECOMMENDATION_BANNER_HIDDEN_UNTIL
        : LocalStorageKeys.GOAL_RECOMMENDATION_COUNT_BADGE_HIDDEN_UNTIL;
    this.writeToLocalStorage(userId, key, isoDateString);
  };
}

/**
 * @deprecated This hook provides direct localStorage access which can cause render-time side effects.
 * Use the store's init* functions within useEffect instead:
 *
 * For initialization:
 * - useStore(state => state.initIntroIsDone) within useEffect([])
 * - useStore(state => state.initVideoPlaybackSpeed) within useEffect([])
 * - useStore(state => state.initLastScheduleQueueDate) within useEffect([])
 *
 * For getting values during render:
 * - useStore(state => state.introIsDone)
 * - useStore(state => state.videoPlaybackSpeed)
 * - useStore(state => state.lastScheduleQueueDate)
 *
 * For setting values:
 * - useStore(state => state.setIntroIsDone)
 * - useStore(state => state.setVideoPlaybackSpeed)
 * - useStore(state => state.setLastScheduleQueueDate)
 */
const useLocalStorage = () => {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      'useLocalStorage hook is deprecated. Use store init* functions within useEffect instead.'
    );
  }

  const localStorageService = LocalStorageService.getInstance();

  return {
    /**
     * @deprecated Use store.initIntroIsDone within useEffect, then access store.introIsDone
     */
    getIntroIsDone: localStorageService.getIntroIsDone,
    setIntroIsDone: localStorageService.setIntroIsDone,
    /**
     * @deprecated Use store.initLastScheduleQueueDate within useEffect, then access store.lastScheduleQueueDate
     */
    getLastScheduleQueueDate: localStorageService.getLastScheduleQueueDate,
    setLastScheduleQueueDate: localStorageService.setLastScheduleQueueDate,
    /**
     * @deprecated Use store.initVideoPlaybackSpeed within useEffect, then access store.videoPlaybackSpeed
     */
    getVideoPlaybackSpeed: localStorageService.getVideoPlaybackSpeed,
    setVideoPlaybackSpeed: localStorageService.setVideoPlaybackSpeed,
    // TOC section collapse state methods
    isTocSectionCollapsed: localStorageService.isTocSectionCollapsed,
    setTocSectionCollapsed: localStorageService.setTocSectionCollapsed,
    // Last node entity ID methods
    getLastNodeEntityId: localStorageService.getLastNodeEntityId,
    setLastNodeEntityId: localStorageService.setLastNodeEntityId,
    // Goal recommendation count hidden until (banner or badge, hide for one week)
    getGoalRecommendationCountHidden: localStorageService.getGoalRecommendationCountHidden,
    setGoalRecommendationCountHidden: localStorageService.setGoalRecommendationCountHidden
  };
};

export { useLocalStorage };
